import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AppLayout from '../components/layout/AppLayout';
import {
  createShopCampaignBonus,
  listShopCampaignBonusGrants,
  listShopCampaignBonuses,
  updateShopCampaignBonus,
} from '../services/api';
import {
  bonusLabel,
  paidLabel,
  slotLabel,
  money,
  type CampaignBonus,
} from './depositBonusDisplay';

type Grant = {
  id: string;
  userId?: string;
  walletTransactionId?: string | null;
  usdWalletTransactionId?: string | null;
  username: string | null;
  email: string | null;
  depositAmount: number;
  bonusAmount: number;
  grantedCount: number;
  reservedCount: number;
};

type Draft = {
  name: string;
  playerMessage: string;
  application: CampaignBonus['application'];
  bonusType: CampaignBonus['bonusType'];
  bonusValue: string;
  minDeposit: string;
  maxBonusPerDeposit: string;
  maxBonusPerPlayer: string;
  maxRedemptionsPerPlayer: string;
  startsAt: string;
  endsAt: string;
  status: CampaignBonus['status'];
};

function localInput(iso?: string) {
  const date = iso ? new Date(iso) : new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function blankDraft(): Draft {
  const end = new Date();
  end.setDate(end.getDate() + 30);
  return {
    name: '',
    playerMessage: '',
    application: 'every_deposit',
    bonusType: 'percentage',
    bonusValue: '10',
    minDeposit: '10',
    maxBonusPerDeposit: '20',
    maxBonusPerPlayer: '',
    maxRedemptionsPerPlayer: '',
    startsAt: localInput(),
    endsAt: localInput(end.toISOString()),
    status: 'draft',
  };
}

function draftFrom(campaign: CampaignBonus): Draft {
  return {
    name: campaign.name,
    playerMessage: campaign.playerMessage,
    application: campaign.application,
    bonusType: campaign.bonusType,
    bonusValue: String(campaign.bonusValue),
    minDeposit: String(campaign.minDeposit),
    maxBonusPerDeposit: String(campaign.maxBonusPerDeposit),
    maxBonusPerPlayer: campaign.maxBonusPerPlayer == null ? '' : String(campaign.maxBonusPerPlayer),
    maxRedemptionsPerPlayer: campaign.maxRedemptionsPerPlayer == null ? '' : String(campaign.maxRedemptionsPerPlayer),
    startsAt: localInput(campaign.startsAt),
    endsAt: localInput(campaign.endsAt),
    status: campaign.status,
  };
}

function payload(draft: Draft) {
  return {
    ...draft,
    bonusValue: Number(draft.bonusValue),
    minDeposit: Number(draft.minDeposit),
    maxBonusPerDeposit: draft.bonusType === 'fixed' ? Number(draft.bonusValue) : Number(draft.maxBonusPerDeposit),
    maxBonusPerPlayer: draft.maxBonusPerPlayer === '' ? null : Number(draft.maxBonusPerPlayer),
    maxRedemptionsPerPlayer: draft.application === 'first_deposit' || draft.maxRedemptionsPerPlayer === ''
      ? null
      : Number(draft.maxRedemptionsPerPlayer),
    startsAt: new Date(draft.startsAt).toISOString(),
    endsAt: new Date(draft.endsAt).toISOString(),
  };
}

function errorMessage(err: unknown) {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { data?: { message?: string } } }).response?.data?.message;
  }
  return undefined;
}

function endsLabel(iso: string) {
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return '';
  return `Ends ${new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(end)}`;
}

function applicationLabel(application: CampaignBonus['application']) {
  return application === 'first_deposit' ? 'First deposit' : 'Every deposit';
}

function offerLocked(campaign: CampaignBonus | null) {
  if (!campaign) return false;
  return campaign.status !== 'draft' || campaign.grantedAmount > 0 || campaign.reservedAmount > 0;
}

// An every-deposit campaign with no per-Player limit can be farmed by depositing,
// cashing out and depositing again. The server refuses it until the campaign has run.
function needsPlayerLimit(draft: Pick<Draft, 'application' | 'maxBonusPerPlayer' | 'maxRedemptionsPerPlayer'>) {
  return draft.application === 'every_deposit'
    && draft.maxBonusPerPlayer.trim() === ''
    && draft.maxRedemptionsPerPlayer.trim() === '';
}

function startsLocked(campaign: CampaignBonus | null) {
  if (!offerLocked(campaign) || !campaign) return false;
  return new Date(campaign.startsAt).getTime() <= Date.now();
}

function statusClass(status: CampaignBonus['status']) {
  if (status === 'active') return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200';
  if (status === 'paused') return 'bg-amber-100 text-amber-800 ring-1 ring-amber-200';
  if (status === 'ended') return 'bg-stone-100 text-stone-500 ring-1 ring-stone-200';
  return 'bg-sky-50 text-sky-800 ring-1 ring-sky-200';
}

const fieldClass = 'mt-1 w-full rounded-lg border border-stone-200 bg-white px-2.5 py-2 text-sm text-stone-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-500';

export default function DepositBonusPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams();
  const mode = location.pathname.endsWith('/new') ? 'new' : campaignId ? 'detail' : 'list';
  const [campaigns, setCampaigns] = useState<CampaignBonus[]>([]);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [draft, setDraft] = useState<Draft>(blankDraft);
  const [loading, setLoading] = useState(true);
  const selected = campaigns.find((campaign) => campaign.id === campaignId) ?? null;
  const running = campaigns.filter((campaign) => campaign.status === 'active');
  const creditsPaid = running.reduce((sum, campaign) => sum + campaign.grantedAmount, 0);
  const held = running.reduce((sum, campaign) => sum + campaign.reservedAmount, 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listShopCampaignBonuses();
      setCampaigns(response.data?.campaigns || []);
    } catch (err) {
      toast.error(errorMessage(err) || 'Could not load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (mode === 'new') setDraft(blankDraft());
  }, [mode]);

  useEffect(() => {
    if (mode !== 'detail' || !selected) return;
    setDraft(draftFrom(selected));
  }, [mode, selected]);

  useEffect(() => {
    if (mode !== 'detail' || !campaignId) {
      setGrants([]);
      return;
    }
    listShopCampaignBonusGrants(campaignId)
      .then((response) => setGrants(response.data?.grants || []))
      .catch(() => setGrants([]));
  }, [mode, campaignId]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!(mode === 'detail' && offerLocked(selected)) && needsPlayerLimit(draft)) return;
    try {
      const body = payload(draft);
      const response = mode === 'detail' && campaignId
        ? await updateShopCampaignBonus(campaignId, body)
        : await createShopCampaignBonus(body);
      toast.success('Campaign saved');
      await load();
      const saved = response.data?.campaign;
      if (saved && mode === 'new') navigate(`/deposit-bonus/${saved.id}`);
    } catch (err) {
      toast.error(errorMessage(err) || 'Could not save this campaign');
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 p-4">
        {mode === 'list' ? (
          <ListView
            loading={loading}
            campaigns={campaigns}
            runningCount={running.length}
            creditsPaid={creditsPaid}
            held={held}
            onOpen={(id) => navigate(`/deposit-bonus/${id}`)}
            onCreate={() => navigate('/deposit-bonus/new')}
          />
        ) : (
          <EditorView
            mode={mode}
            loading={loading}
            missing={mode === 'detail' && !loading && !selected}
            draft={draft}
            setDraft={setDraft}
            grants={grants}
            selected={selected}
            onBack={() => navigate('/deposit-bonus')}
            onSave={(event) => void save(event)}
          />
        )}
      </div>
    </AppLayout>
  );
}

function ListView({
  loading,
  campaigns,
  runningCount,
  creditsPaid,
  held,
  onOpen,
  onCreate,
}: {
  loading: boolean;
  campaigns: CampaignBonus[];
  runningCount: number;
  creditsPaid: number;
  held: number;
  onOpen: (id: string) => void;
  onCreate: () => void;
}) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Deposit bonus campaign</h1>
          <p className="mt-1 text-sm text-muted-foreground">Only your Players. These do not stack with platform bonuses.</p>
        </div>
        <button type="button" className="rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90" onClick={onCreate}>
          New campaign
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Running" value={String(runningCount)} tone="sky" />
        <Stat label="Credits paid" value={money(creditsPaid)} tone="emerald" />
        <Stat label="Held" value={money(held)} tone="amber" />
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 text-xs font-medium text-stone-500">
              <tr>
                <th className="px-3 py-2.5">Campaign</th>
                <th className="px-3 py-2.5">Bonus</th>
                <th className="px-3 py-2.5">Ends</th>
                <th className="px-3 py-2.5">Credits paid</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && <tr><td className="px-3 py-6 text-muted-foreground" colSpan={5}>No campaigns yet.</td></tr>}
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="cursor-pointer border-t border-stone-100 transition hover:bg-sky-50/70" onClick={() => onOpen(campaign.id)}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-stone-900">{campaign.name}</div>
                    <div className="text-xs text-stone-500">{applicationLabel(campaign.application)}</div>
                  </td>
                  <td className="px-3 py-3 font-medium text-emerald-700">{bonusLabel(campaign)}</td>
                  <td className="px-3 py-3 text-stone-500">{endsLabel(campaign.endsAt)}</td>
                  <td className="px-3 py-3 font-medium text-stone-800">{paidLabel(campaign)}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusClass(campaign.status)}`}>{campaign.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'sky' | 'emerald' | 'amber' }) {
  const card = {
    sky: 'border-sky-200 bg-sky-50',
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
  }[tone];
  const amount = {
    sky: 'text-sky-800',
    emerald: 'text-emerald-800',
    amber: 'text-amber-800',
  }[tone];
  return (
    <div className={`rounded-xl border px-3 py-3 shadow-sm ${card}`}>
      <div className={`text-xs font-medium ${amount}`}>{label}</div>
      <div className={`mt-1 text-lg font-semibold ${amount}`}>{value}</div>
    </div>
  );
}

function EditorView({
  mode,
  loading,
  missing,
  draft,
  setDraft,
  grants,
  selected,
  onBack,
  onSave,
}: {
  mode: 'new' | 'detail';
  loading: boolean;
  missing: boolean;
  draft: Draft;
  setDraft: (draft: Draft) => void;
  grants: Grant[];
  selected: CampaignBonus | null;
  onBack: () => void;
  onSave: (event: FormEvent) => void;
}) {
  const lockOffer = mode === 'detail' && offerLocked(selected);
  const lockStarts = mode === 'detail' && startsLocked(selected);
  const missingLimit = !lockOffer && needsPlayerLimit(draft);
  return (
    <>
      <div>
        <button type="button" className="text-sm font-medium text-sky-700 hover:text-sky-900" onClick={onBack}>Campaigns</button>
        <h1 className="mt-1 text-xl font-semibold text-stone-900">{mode === 'new' ? 'New campaign' : (selected?.name || 'Campaign')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Saving does not change bonus a Player has already received.</p>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : missing ? (
        <p className="text-sm text-muted-foreground">This campaign is not on the list.</p>
      ) : (
        <form className="space-y-6 rounded-xl border border-stone-200 bg-white p-4 shadow-sm" onSubmit={onSave}>
          <section className="space-y-3">
            <h2 className="border-l-2 border-sky-400 pl-2 text-sm font-semibold text-stone-900">What the Player sees</h2>
            <Field label="Name" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
            <Field label="What the Player sees" value={draft.playerMessage} onChange={(playerMessage) => setDraft({ ...draft, playerMessage })} />
          </section>
          <section className="space-y-3">
            <h2 className="border-l-2 border-emerald-400 pl-2 text-sm font-semibold text-stone-900">The offer</h2>
            {lockOffer && (
              <p className="text-xs text-stone-500">To change the bonus, end this campaign and create a new one.</p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs font-medium text-stone-600">
                Applies to
                <select className={fieldClass} disabled={lockOffer} value={draft.application} onChange={(event) => setDraft({ ...draft, application: event.target.value as Draft['application'] })}>
                  <option value="every_deposit">Every deposit</option>
                  <option value="first_deposit">First deposit in this campaign</option>
                </select>
              </label>
              <label className="block text-xs font-medium text-stone-600">
                Bonus
                <select className={fieldClass} disabled={lockOffer} value={draft.bonusType} onChange={(event) => setDraft({ ...draft, bonusType: event.target.value as Draft['bonusType'] })}>
                  <option value="percentage">Percentage of the deposit</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </label>
              <Field label={draft.bonusType === 'fixed' ? 'Amount ($)' : 'Percent'} value={draft.bonusValue} disabled={lockOffer} onChange={(bonusValue) => setDraft({ ...draft, bonusValue })} />
              <Field label="Minimum deposit ($)" value={draft.minDeposit} disabled={lockOffer} onChange={(minDeposit) => setDraft({ ...draft, minDeposit })} />
              {draft.bonusType === 'percentage' && (
                <Field label="Most one deposit can earn ($)" value={draft.maxBonusPerDeposit} disabled={lockOffer} onChange={(maxBonusPerDeposit) => setDraft({ ...draft, maxBonusPerDeposit })} />
              )}
            </div>
            {draft.bonusType === 'percentage' && (
              <p className="text-xs text-muted-foreground">10 means 10% of the deposit.</p>
            )}
            {draft.application === 'every_deposit' && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Total bonus per Player ($)" value={draft.maxBonusPerPlayer} disabled={lockOffer} onChange={(maxBonusPerPlayer) => setDraft({ ...draft, maxBonusPerPlayer })} />
                  <Field label="Times per Player" value={draft.maxRedemptionsPerPlayer} disabled={lockOffer} onChange={(maxRedemptionsPerPlayer) => setDraft({ ...draft, maxRedemptionsPerPlayer })} />
                </div>
                {missingLimit ? (
                  <p className="text-xs font-medium text-red-700">Set a limit per Player: a total bonus cap, a number of times, or both.</p>
                ) : !lockOffer && (
                  <p className="text-xs text-muted-foreground">At least one limit is required for every deposit.</p>
                )}
              </>
            )}
            {draft.application === 'first_deposit' && (
              <p className="text-xs text-muted-foreground">Only one first-deposit campaign can cover these dates. The next one can start when this one ends. A deposit under the minimum does not use that spot.</p>
            )}
          </section>
          <section className="space-y-3">
            <h2 className="border-l-2 border-amber-400 pl-2 text-sm font-semibold text-stone-900">When</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Starts" type="datetime-local" value={draft.startsAt} disabled={lockStarts} onChange={(startsAt) => setDraft({ ...draft, startsAt })} />
              <Field label="Ends" type="datetime-local" value={draft.endsAt} onChange={(endsAt) => setDraft({ ...draft, endsAt })} />
              <label className="block text-xs font-medium text-stone-600">
                Status
                <select className={fieldClass} value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as Draft['status'] })}>
                  {(!lockOffer || draft.status === 'draft') && <option value="draft">Draft</option>}
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="ended">Ended</option>
                </select>
              </label>
            </div>
          </section>
          <button type="submit" disabled={missingLimit} className="rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">Save</button>
        </form>
      )}
      {mode === 'detail' && !missing && (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <h2 className="border-b border-emerald-100 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900">Credits paid to Players</h2>
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 text-xs font-medium text-stone-500">
              <tr>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Deposit</th>
                <th className="px-3 py-2">Bonus</th>
                <th className="px-3 py-2">Uses left</th>
              </tr>
            </thead>
            <tbody>
              {grants.length === 0 && <tr><td className="px-3 py-4 text-muted-foreground" colSpan={4}>No one has received this yet.</td></tr>}
              {grants.map((grant) => (
                <tr key={grant.id} className="border-t border-stone-100">
                  <td className="px-3 py-2">
                    {grant.userId ? (
                      <Link to={`/players/${grant.userId}`} className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800">
                        {grant.username || grant.email || 'Player'}
                      </Link>
                    ) : (
                      <span className="text-stone-900">{grant.username || grant.email || 'Player'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {grant.usdWalletTransactionId ? (
                      <Link to={`/online-transactions?usd=${encodeURIComponent(grant.usdWalletTransactionId)}`} className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800">
                        {money(grant.depositAmount)}
                      </Link>
                    ) : (
                      <span className="text-stone-600">{money(grant.depositAmount)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {grant.walletTransactionId ? (
                      <Link to={`/transactions?tx=${encodeURIComponent(grant.walletTransactionId)}`} className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800">
                        {money(grant.bonusAmount)}
                      </Link>
                    ) : (
                      <span className="font-medium text-emerald-700">{money(grant.bonusAmount)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{selected ? slotLabel(selected, grant) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Field({ label, value, onChange, type = 'text', disabled = false }: { label: string; value: string; onChange: (value: string) => void; type?: string; disabled?: boolean }) {
  return (
    <label className="block text-xs font-medium text-stone-600">
      {label}
      <input className={fieldClass} type={type} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
